import { lazy, Suspense, useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Mail, AlertTriangle } from 'lucide-react';

const TwoFactorSetup = lazy(() =>
  import('@/components/TwoFactorSetup').then((module) => ({
    default: module.TwoFactorSetup,
  })),
);

interface TwoFactorGuardProps {
  children: React.ReactNode;
}

export function TwoFactorGuard({ children }: TwoFactorGuardProps) {
  const [location] = useLocation();
  const [showSetup, setShowSetup] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');

  // Don't check 2FA for public pages - allow immediate access
  const publicPages = [
    '/',
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/courses',
    '/documentation',
    '/contact',
    '/privacy',
    '/terms',
    '/starter-kits',
    '/roadmap',
    '/playground',
    '/career',
    '/certifications',
    '/help',
    '/support',
    '/payment-plans',
    '/blog',
    '/community',
    '/research',
    '/reports',
    '/accessibility',
  ];
  const publicPrefixes = ['/track/', '/course/', '/learn/'];
  const isPublicPage = publicPages.includes(location) ||
    publicPrefixes.some(prefix => location.startsWith(prefix));

  useEffect(() => {
    if (isPublicPage) {
      setIsChecking(false);
      setShowEmailVerification(false);
      return;
    }
    
    // Only check 2FA for authenticated areas
    setIsChecking(true);
    checkTwoFactorStatus();
  }, [location, isPublicPage]);

  const checkTwoFactorStatus = async () => {
    try {
      const response = await fetch('/api/auth/2fa-status', {
        credentials: 'include'
      });

      if (response.status === 403) {
        const data = await response.json();
        if (data.requiresTwoFactorSetup || data.requires2FASetup) {
          // Get user info for 2FA setup
          const userResponse = await fetch('/api/me', { credentials: 'include' });
          if (userResponse.ok) {
            const userData = await userResponse.json();
            setUserEmail(userData.user?.email || '');
          }
          setShowSetup(true);
        }
      } else if (response.ok) {
        const data = await response.json();
        if (!data.enabled) {
          // 2FA not enabled - mandatory setup required
          const userResponse = await fetch('/api/me', { credentials: 'include' });
          if (userResponse.ok) {
            const userData = await userResponse.json();
            setUserEmail(userData.user?.email || '');
          }
          setShowSetup(true);
        } else if (data.requiresEmailVerification) {
          const userResponse = await fetch('/api/me', { credentials: 'include' });
          if (userResponse.ok) {
            const userData = await userResponse.json();
            setUserEmail(userData.user?.email || '');
          }
          setShowSetup(false);
          setShowEmailVerification(true);
        }
      }
    } catch (error) {
      console.error('2FA status check failed:', error);
    } finally {
      setIsChecking(false);
    }
  };

  const handleSetupComplete = () => {
    setShowSetup(false);
    checkTwoFactorStatus();
  };

  const resendVerification = async () => {
    setIsResending(true);
    setEmailMessage('');

    try {
      const response = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error('Unable to send verification email');
      }

      setEmailMessage('Verification email sent. Please check your inbox.');
    } catch (error) {
      setEmailMessage('Could not send verification email. Please try again later.');
    } finally {
      setIsResending(false);
    }
  };

  if (isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-900 dark:to-purple-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-gray-600 dark:text-gray-400">Checking security status...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showSetup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-900 dark:to-purple-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-4">
          <Alert className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-800 dark:text-orange-200">
              <strong>Security Required:</strong> Two-factor authentication setup is mandatory for all accounts. 
              You cannot access the platform without completing this security setup.
            </AlertDescription>
          </Alert>
          
          <Suspense fallback={null}>
            <TwoFactorSetup 
              userEmail={userEmail}
              onComplete={handleSetupComplete}
            />
          </Suspense>
        </div>
      </div>
    );
  }

  if (showEmailVerification) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-900 dark:to-purple-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Mail className="w-12 h-12 mx-auto mb-2 text-blue-600" />
            <CardTitle>Verify Your Email</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-sm text-muted-foreground">
              We sent a confirmation link to {userEmail || 'your email address'}. Verify your email before accessing the platform.
            </p>
            <Button onClick={resendVerification} disabled={isResending} className="w-full">
              {isResending ? 'Sending...' : 'Resend Verification Email'}
            </Button>
            {emailMessage && (
              <Alert>
                <AlertDescription>{emailMessage}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
